import React from 'react';
import { Link } from 'react-router-dom';
import {
  X, ExternalLink, ShieldCheck, ShieldAlert, ShieldQuestion, Link2, RotateCcw,
  CreditCard, Monitor, Package, Cable, Paperclip, Megaphone, Wrench, Check,
} from 'lucide-react';
import { isInWarranty, warrantyDaysLeft } from '@platform/shared-types';
import ShimmerImage from '../../components/ShimmerImage';
import Barcode from '../../components/Barcode';
import {
  CATEGORY_LABEL, UNIT_STATUS_LABEL, UNIT_STATUS_CHIP,
  type DeviceModelRow, type DeviceUnitRow,
} from '../../services/hardwareApi';

/**
 * ONE PHYSICAL DEVICE, AT A GLANCE — the popup a unit row opens on click.
 *
 * DELIBERATELY UNIT-SCOPED, not model-scoped. `DeviceQuickView` next door
 * answers "what is this product" from the catalog; every question asked while
 * standing at a bench is about THIS box — which build is on it, how far its
 * checklist got, what it is paired with, whether it is still under warranty.
 * A catalog popup would answer none of them, and the rows that open this are
 * rows about a serial.
 *
 * Read-only, like its neighbour: every write already has a surface, and a
 * popup that can change things is a popup people are afraid to open.
 *
 * The same component is opened from the device list at the top of the card and
 * from the set-up lanes at the bottom, so the two cannot describe the same
 * device differently.
 */

const CATEGORY_ICON: Record<string, React.FC<any>> = {
  pos_terminal: Monitor, payment_device: CreditCard, peripheral: Package, accessory: Cable,
  office_supply: Paperclip, marketing_material: Megaphone, tools: Wrench,
};

const REPLACEMENT_LABEL: Record<string, string> = {
  returned: 'returned', lost_in_transit: 'lost in transit', warranty: 'warranty', damaged: 'damaged',
};

const when = (ts?: number) => (ts
  ? new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—');
const day = (ts?: number) => (ts
  ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : '—');

const Fact: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({ label, children, mono }) => (
  <div className="min-w-0">
    <div className="text-[9.5px] font-semibold uppercase tracking-wide text-stone-400">{label}</div>
    <div className={`truncate text-xs text-stone-800 ${mono ? 'font-mono' : ''}`}>{children}</div>
  </div>
);

/**
 * WARRANTY, SAID IN THREE WAYS BECAUSE THERE ARE THREE ANSWERS.
 *
 * "Unknown" is its own state and must never be drawn as "expired". A device
 * delivered before the clock existed has no record of when it arrived and that
 * cannot be reconstructed — telling somebody it is out of cover would be
 * inventing a fact that decides whether a customer gets a free replacement.
 */
const Warranty: React.FC<{ unit: DeviceUnitRow }> = ({ unit }) => {
  if (typeof unit.warrantyUntil !== 'number') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-[10.5px] font-semibold text-stone-500">
        <ShieldQuestion size={11} /> Warranty unknown
      </span>
    );
  }
  const left = warrantyDaysLeft(unit) ?? 0;
  return isInWarranty(unit) ? (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10.5px] font-semibold text-emerald-700">
      <ShieldCheck size={11} /> In warranty · {left} day{left === 1 ? '' : 's'} left
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10.5px] font-semibold text-amber-800">
      <ShieldAlert size={11} /> Out of warranty · ended {day(unit.warrantyUntil)}
    </span>
  );
};

const UnitQuickView: React.FC<{
  unit: DeviceUnitRow;
  model?: DeviceModelRow;
  /** Every unit on the same order, so a coupled partner can be named rather than shown as an id. */
  siblings?: DeviceUnitRow[];
  onClose: () => void;
  /** Swap the popup to another device without closing it — the coupled partner. */
  onOpenUnit?: (unitId: string) => void;
}> = ({ unit, model, siblings = [], onClose, onOpenUnit }) => {
  const Icon = CATEGORY_ICON[model?.category || ''] || Package;
  const ref = unit.trackingId || unit.serialNumber || unit.unitId;

  const steps = unit.provisioning?.steps || [];
  const required = steps.filter((s) => s.required);
  const counted = required.length > 0 ? required : steps;
  const done = counted.filter((s) => s.done).length;

  const partner = unit.coupledUnitId
    ? siblings.find((s) => s.unitId === unit.coupledUnitId)
    : undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Device ${ref}`}
      >
        <header className="flex items-start gap-3 border-b border-stone-100 p-4">
          <ShimmerImage
            src={model?.media?.heroImageUrl}
            alt={model?.modelName || ref}
            width={56}
            height={56}
            objectFit="contain"
            className="shrink-0 rounded-xl border border-stone-200"
            fallbackBgColor="bg-stone-100"
            fallbackIcon={<Icon size={22} className="text-stone-400" />}
          />
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[15px] font-bold text-stone-900">{model?.modelName || unit.modelId}</h3>
            <p className="truncate text-[11.5px] text-stone-500">
              {[model?.manufacturer, model?.category ? CATEGORY_LABEL[model.category] : null].filter(Boolean).join(' · ') || '—'}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${UNIT_STATUS_CHIP[unit.state]}`}>
                {UNIT_STATUS_LABEL[unit.state]}
              </span>
              <Warranty unit={unit} />
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="-m-1 shrink-0 rounded-lg p-2 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
          >
            <X size={16} />
          </button>
        </header>

        {/* THE THING YOU SCAN. Same symbol the label printer produces, so a
            unit found on screen and a unit found on a shelf agree. */}
        <div className="flex items-center gap-3 border-b border-stone-100 bg-stone-50/70 px-4 py-3">
          <Barcode value={ref} height={34} moduleWidth={1.5} />
          <div className="min-w-0">
            <div className="font-mono text-[13px] font-bold tabular-nums text-stone-900">{ref}</div>
            {unit.serialNumber && unit.serialNumber !== ref && (
              <div className="font-mono text-[10.5px] text-stone-400">S/N {unit.serialNumber}</div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 p-4">
          <Fact label="RubyTill build" mono>{unit.osInstall?.appBuild || unit.appBuild || '—'}</Fact>
          <Fact label="Android">{unit.osVersion || '—'}</Fact>
          <Fact label="Installed">{when(unit.osInstall?.installedAt)}</Fact>
          <Fact label="Installed by">{unit.osInstall?.by || '—'}</Fact>
          <Fact label="Checklist">
            {counted.length > 0 ? `${done} of ${counted.length} done` : 'Not started'}
          </Fact>
          <Fact label="Delivered">{day(unit.deliveredAt)}</Fact>
        </div>

        {/* PAIRING — the other half of the box that leaves with it. Clickable,
            because "what is it paired with" is nearly always followed by
            "and what state is THAT in". */}
        {unit.coupledUnitId && (
          <div className="border-t border-stone-100 px-4 py-3">
            <div className="mb-1.5 text-[9.5px] font-semibold uppercase tracking-wide text-stone-400">Paired with</div>
            <button
              onClick={() => onOpenUnit?.(unit.coupledUnitId!)}
              disabled={!onOpenUnit || !partner}
              className="flex w-full items-center gap-2 rounded-xl border border-stone-200 px-3 py-2 text-left hover:border-emerald-400 disabled:cursor-default disabled:hover:border-stone-200"
            >
              <Link2 size={14} className="shrink-0 text-stone-400" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-[12px] font-semibold text-stone-800">
                  {partner ? (partner.trackingId || partner.serialNumber || partner.unitId) : unit.coupledUnitId}
                </span>
                {partner && (
                  <span className="block truncate text-[10.5px] text-stone-500">
                    {UNIT_STATUS_LABEL[partner.state]}
                  </span>
                )}
              </span>
              {partner && onOpenUnit && <ExternalLink size={12} className="shrink-0 text-stone-400" />}
            </button>
          </div>
        )}

        {/* LINEAGE — only when there is any. A device that replaced another,
            or was itself replaced, is a different object to reason about. */}
        {(unit.replaces || unit.replacedByUnitId) && (
          <div className="border-t border-stone-100 px-4 py-3">
            {unit.replaces && (
              <p className="flex items-start gap-2 text-[11.5px] text-stone-600">
                <RotateCcw size={12} className="mt-0.5 shrink-0 text-stone-400" />
                <span>
                  Sent out to replace <span className="font-mono font-semibold text-stone-800">{unit.replaces.unitId}</span>
                  {' '}· {REPLACEMENT_LABEL[unit.replaces.reason] || unit.replaces.reason} · {day(unit.replaces.at)}
                </span>
              </p>
            )}
            {unit.replacedByUnitId && (
              <p className="mt-1 flex items-start gap-2 text-[11.5px] text-stone-600">
                <RotateCcw size={12} className="mt-0.5 shrink-0 text-stone-400" />
                <span>
                  Replaced by <span className="font-mono font-semibold text-stone-800">{unit.replacedByUnitId}</span>
                </span>
              </p>
            )}
          </div>
        )}

        {unit.reinstall?.reason && (
          <div className="border-t border-stone-100 bg-amber-50/60 px-4 py-2.5">
            <p className="text-[11.5px] leading-relaxed text-amber-900">
              <span className="font-semibold">Reinstall requested</span> — {unit.reinstall.reason}
            </p>
          </div>
        )}

        <footer className="flex items-center justify-between gap-2 border-t border-stone-100 bg-stone-50/70 px-4 py-3">
          <span className="truncate font-mono text-[10px] text-stone-400">{unit.unitId}</span>
          {/* Everything this popup does NOT do lives on the unit page. One
              link out beats a row of half-actions that each need a gate. */}
          <Link
            to={`/inventory/units/${encodeURIComponent(unit.unitId)}`}
            className="inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-[11.5px] font-semibold text-stone-700 hover:border-emerald-500 hover:text-emerald-700"
          >
            Full device page <ExternalLink size={11} />
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default UnitQuickView;
